// Catalogue des modules que l'usager peut activer ou désactiver, et règles pures
// de visibilité. Source unique pour : la page /bureau/modules, le menu latéral et
// la palette de commandes.
//
// Ce que « désactiver » fait — et ne fait PAS (affiché tel quel à l'usager) :
//   • retire le module du menu et de la palette de commandes, POUR CET USAGER
//     (les cartes de l'accueil ne suivent pas encore ce choix) ;
//   • ne supprime aucune donnée ; réactiver rend tout tel quel ;
//   • n'est pas une barrière de sécurité : les droits d'accès restent ceux de l'organisation,
//     et une adresse connue reste ouverte si l'usager y a droit.

export type ModuleState = 'service' | 'pilote' | 'chantier'

export interface ModuleEntry {
  key: string
  icon: string
  label: string
  desc: string
  /** Page d'entrée du module. */
  href: string
  color: string
  state: ModuleState
  /** Toujours actif : le site ne fonctionne pas sans lui. */
  required?: boolean
  /** Ce que le module apporte quand il est actif. */
  enables: string[]
  /** Préfixes d'adresses retirés du menu quand il est désactivé. */
  prefixes: string[]
}

export const STATE_LABEL: Record<ModuleState, string> = {
  service: 'En service',
  pilote: 'Pilote',
  chantier: 'En chantier',
}

export const MODULES: ModuleEntry[] = [
  {
    key: 'ticoune', icon: '🧢', label: 'Ti-Coune', color: '#60a5fa', state: 'service', required: true,
    desc: "L'assistant IA du site : il répond, retient le contexte et dirige les autres agents.",
    href: '/messages',
    enables: ['Panneau de droite et messagerie IA', 'Réponses sur vos projets, tâches et documents'],
    prefixes: ['/messages', '/memoire'],
  },
  {
    key: 'bureau', icon: '▣', label: 'Bureau', color: '#f472b6', state: 'service', required: true,
    desc: 'Votre espace de travail : projets, tâches, calendrier, documents, contacts, courriers, agent poste.',
    href: '/bureau',
    enables: ['Projets, tâches et calendrier', 'Documents et sauvegarde chiffrée', 'Contacts et courriers'],
    prefixes: ['/bureau', '/aujourdhui', '/projets'],
  },
  {
    key: 'affaires', icon: '💼', label: 'Affaires et relation client', color: '#f97316', state: 'service',
    desc: 'Clients, prospects, opportunités et suivi des affaires de votre organisation.',
    href: '/bureau/crm',
    enables: ['Entrées « CRM » et « Affaires » du menu Bureau', 'Prospects et opportunités'],
    prefixes: ['/bureau/crm', '/bureau/affaires', '/bureau/transformation'],
  },
  {
    key: 'villes', icon: '🏛', label: 'Villes et territoire', color: '#38bdf8', state: 'service',
    desc: "Découverte des villes et de leurs données publiques, puis urbanisme : zonage, marges, permis et rapport de conformité.",
    href: '/technologue/villes',
    enables: ['Fiches de villes (statistiques, divisions)', 'Analyse de zonage, permis et conformité'],
    prefixes: ['/technologue/villes', '/technologue/zonage', '/technologue/permis'],
  },
  {
    key: 'legal', icon: '⚖', label: 'Legal AI', color: '#34d399', state: 'service',
    desc: 'Règlements du Québec, zonage et normes : recherche et raisonnement appuyés sur les textes.',
    href: '/lawyer',
    enables: ['Recherche dans les règlements', 'Vérification de permis'],
    prefixes: ['/lawyer', '/legal'],
  },
  {
    key: 'recherche', icon: '🔍', label: 'Recherche, veille et graphe', color: '#a78bfa', state: 'service',
    desc: 'Recherche web et mémoire, veille sur personnes, organisations et propriétés, et graphe des relations entre elles.',
    href: '/search',
    enables: ['Recherche augmentée (web, mémoire)', 'Veille (OSINT)', 'Graphe de relations des villes et organisations'],
    prefixes: ['/search', '/technologue/graphe', '/osint'],
  },
  {
    key: 'spatial', icon: '⚓', label: 'Amiral3D', color: '#a78bfa', state: 'chantier',
    desc: 'Jumeau spatial : BIM/GIS, enveloppe constructible, export glTF/IFC.',
    href: '/spatial',
    enables: ['Vue 3D du territoire', 'Enveloppe constructible'],
    prefixes: ['/spatial'],
  },
  {
    key: 'automation', icon: '⟳', label: 'Automatisations', color: '#f5a623', state: 'pilote',
    desc: 'Flux de travail IA déclenchés par le temps ou des événements.',
    href: '/automation',
    enables: ['Entrée « Automatisations » du menu', 'Création de flux planifiés'],
    prefixes: ['/automation'],
  },
  {
    key: 'bourse', icon: '📈', label: 'Bourse et placements', color: '#34d399', state: 'pilote',
    desc: 'Signaux et suivi de placements en simulation. Aucune transaction réelle.',
    href: '/bourse',
    enables: ['Entrée « Bourse & placements » du menu', 'Simulation et alertes'],
    prefixes: ['/bourse'],
  },
  {
    key: 'jeu', icon: '⬡', label: 'Jeu RPG', color: '#f59e0b', state: 'pilote',
    desc: 'Quiz québécois avec personnages autonomes, points et progression.',
    href: '/jeu',
    enables: ['Accès au jeu depuis le menu et la palette de commandes'],
    prefixes: ['/jeu'],
  },
]

export interface SpecialistAgent {
  key: string
  label: string
  role: string
  /** Ce que l'agent fait quand il est activé pour vous. */
  does: string
}

// Les agents spécialistes que Ti-Coune peut mobiliser. La liste est celle de
// l'ancienne « marketplace », sans notes ni téléchargements inventés.
export const SPECIALISTS: SpecialistAgent[] = [
  { key: 'agent:sysadmin', label: 'Administrateur système', role: 'Appareils et serveurs', does: 'Diagnostics, fichiers et surveillance de vos appareils reliés.' },
  { key: 'agent:seo', label: 'SEO et liens', role: 'Sites web', does: 'Audit de site, liens brisés, mots-clés.' },
  { key: 'agent:recherche', label: 'Recherche web privée', role: 'Recherche', does: 'Recherche via SearXNG auto-hébergé, synthèse et extraction de contenu.' },
  { key: 'agent:memoire', label: 'Gestionnaire de mémoire', role: 'Contexte', does: 'Garde le contexte court terme, sémantique et structuré.' },
  { key: 'agent:nouvelles', label: 'Nouvelles Québec', role: 'Médias', does: 'Regroupe et résume les nouvelles du Québec et du Canada.' },
  { key: 'agent:traduction', label: 'Traduction', role: 'Langues', does: 'Traduction locale français ↔ anglais ↔ espagnol.' },
  { key: 'agent:spatial', label: 'Simulation urbaine', role: 'Territoire', does: 'Bâtiments procéduraux et conformité réglementaire (pilote).' },
  { key: 'agent:compatibilite', label: 'Compatibilité sociale', role: 'Social', does: 'Score de compatibilité et idées d’introduction (pilote).' },
]

const ALL_KEYS = new Set<string>([...MODULES.map(m => m.key), ...SPECIALISTS.map(a => a.key)])
const REQUIRED = new Set(MODULES.filter(m => m.required).map(m => m.key))

/** Liste sûre : clés connues seulement, jamais un module obligatoire, sans doublon. */
export function normalizeDisabled(list: unknown): string[] {
  if (!Array.isArray(list)) return []
  const out = new Set<string>()
  for (const k of list) if (typeof k === 'string' && ALL_KEYS.has(k) && !REQUIRED.has(k)) out.add(k)
  return [...out].sort()
}

export function isEnabled(disabled: readonly string[], key: string): boolean {
  return REQUIRED.has(key) || !disabled.includes(key)
}

/** Bascule un module ; refuse (retourne la liste inchangée) un module obligatoire ou inconnu. */
export function toggled(disabled: readonly string[], key: string): string[] {
  if (REQUIRED.has(key) || !ALL_KEYS.has(key)) return normalizeDisabled(disabled)
  const next = disabled.includes(key) ? disabled.filter(k => k !== key) : [...disabled, key]
  return normalizeDisabled(next)
}

function pathOf(href: string): string {
  return href.split(/[?#]/)[0].replace(/\/+$/, '') || '/'
}

/** Module propriétaire d'une adresse (préfixe le plus long), ou undefined si aucun. */
export function moduleForHref(href: string): ModuleEntry | undefined {
  const p = pathOf(href)
  let best: ModuleEntry | undefined
  let bestLen = -1
  for (const m of MODULES) {
    for (const pre of m.prefixes) {
      if ((p === pre || p.startsWith(pre + '/')) && pre.length > bestLen) { best = m; bestLen = pre.length }
    }
  }
  return best
}

/** Une entrée de menu reste visible sauf si son module est désactivé. */
export function isHrefVisible(disabled: readonly string[], href: string): boolean {
  const m = moduleForHref(href)
  return !m || isEnabled(disabled, m.key)
}

export function enabledModules(disabled: readonly string[]): ModuleEntry[] {
  return MODULES.filter(m => isEnabled(disabled, m.key))
}
